import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../config/supabase';
import Sidebar from '../components/admin/Sidebar';
import TopNavbar from '../components/admin/TopNavbar';
import CategoryTabs from '../components/admin/menu/CategoryTabs';
import SearchAndSortBar from '../components/admin/menu/SearchAndSortBar';
import MenuItemCard from '../components/admin/menu/MenuItemCard';
import AddEditItemModal from '../components/admin/menu/AddEditItemModal';
import CategoriesModal from '../components/admin/menu/CategoriesModal';
import ConfirmModal from '../components/common/ConfirmModal';
import SuccessToast from '../components/common/SuccessToast';
import './MenuManagement.css';

const MenuManagement = () => {
  const navigate = useNavigate();
  const [adminSession, setAdminSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, item: null });
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [categories, setCategories] = useState(['All']);

  useEffect(() => {
    const session = localStorage.getItem('adminSession') || sessionStorage.getItem('adminSession');
    if (!session) {
      navigate('/admin');
      return;
    }
    setAdminSession(JSON.parse(session));
  }, [navigate]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchMenuItems();
  }, [activeCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching categories:', error);
        // Fallback to default categories if Supabase fails
        setCategories(['All', 'Pizza', 'Burgers', 'Chicken', 'Chinese', 'Drinks', 'Desserts']);
        return;
      }

      if (data && data.length > 0) {
        const categoryNames = data.map(cat => cat.name);
        setCategories(['All', ...categoryNames]);
      } else {
        // If no categories exist, use defaults
        setCategories(['All', 'Pizza', 'Burgers', 'Chicken', 'Chinese', 'Drinks', 'Desserts']);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(['All', 'Pizza', 'Burgers', 'Chicken', 'Chinese', 'Drinks', 'Desserts']);
    }
  };

  const handleCategoryChange = (newCategories) => {
    setCategories(newCategories);
  };

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      // Check if Supabase is configured
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        // Demo mode - return empty array or show message
        console.log('Demo mode: Supabase not configured');
        setMenuItems([]);
        setLoading(false);
        return;
      }

      // Fetch from Supabase
      let query = supabase
        .from('menu_items')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply category filter
      if (activeCategory !== 'All') {
        query = query.eq('category', activeCategory);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching menu items:', error);
        setMenuItems([]);
        setLoading(false);
        return;
      }

      // Transform database data to component format
      let transformedItems = (data || []).map(item => {
        // Handle images_urls - could be array, string, or null
        let imagesArray = [];
        if (item.images_urls) {
          if (Array.isArray(item.images_urls)) {
            imagesArray = item.images_urls.filter(img => img && img.trim() !== '');
          } else if (typeof item.images_urls === 'string') {
            // Try to parse if it's a JSON string
            try {
              const parsed = JSON.parse(item.images_urls);
              imagesArray = Array.isArray(parsed) ? parsed.filter(img => img && img.trim() !== '') : [];
            } catch {
              // If not JSON, treat as single image URL
              imagesArray = item.images_urls.trim() ? [item.images_urls] : [];
            }
          }
        }

        // Handle tags - ensure it's an array
        let tagsArray = [];
        if (item.tags) {
          if (Array.isArray(item.tags)) {
            tagsArray = item.tags;
          } else if (typeof item.tags === 'string') {
            try {
              const parsed = JSON.parse(item.tags);
              tagsArray = Array.isArray(parsed) ? parsed : [];
            } catch {
              tagsArray = [];
            }
          }
        }

        console.log('Transforming item:', {
          name: item.name,
          images_urls: item.images_urls,
          imagesArray: imagesArray,
          imagesType: typeof item.images_urls,
          isArray: Array.isArray(item.images_urls)
        });

        return {
          id: item.id,
          name: item.name,
          category: item.category,
          description: item.description || '',
          price: item.price || 0,
          cookingTime: item.cooking_time || 0,
          images: imagesArray,
          tags: tagsArray,
          isAvailable: item.is_available !== undefined ? item.is_available : true,
          isFeatured: item.is_featured || false,
          isPopular: item.is_popular || false,
          isNew: item.is_new || false,
          rating: item.rating || 0,
          reviewsCount: item.reviews_count || 0,
          customizations: item.customizations || {
            sizes: [],
            spiceLevels: [],
            addons: []
          }
        };
      });

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        transformedItems = transformedItems.filter(item =>
          item.name.toLowerCase().includes(query) ||
          (item.description && item.description.toLowerCase().includes(query))
        );
      }

      setMenuItems(transformedItems);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    navigate('/admin/menu/add');
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setIsAddModalOpen(true);
  };

  const handleDeleteClick = (item) => {
    setDeleteConfirm({ isOpen: true, item });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.item) return;

    const itemId = deleteConfirm.item.id;
    const itemName = deleteConfirm.item.name;

    try {
      // Check if Supabase is configured
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        // Demo mode - just remove from state
        setMenuItems(prevItems => prevItems.filter(item => item.id !== itemId));
        setDeleteConfirm({ isOpen: false, item: null });
        setSuccessMessage(`"${itemName}" has been deleted successfully!`);
        setShowSuccessToast(true);
        return;
      }

      // Delete from Supabase
      const { error } = await supabase
        .from('menu_items')
        .delete()
        .eq('id', itemId);

      if (error) {
        throw error;
      }

      // Remove from state
      setMenuItems(prevItems => prevItems.filter(item => item.id !== itemId));
      setDeleteConfirm({ isOpen: false, item: null });
      setSuccessMessage(`"${itemName}" has been deleted successfully!`);
      setShowSuccessToast(true);
    } catch (error) {
      console.error('Error deleting menu item:', error);
      setDeleteConfirm({ isOpen: false, item: null });
      setSuccessMessage('Failed to delete menu item. Please try again.');
      setShowSuccessToast(true);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, item: null });
  };

  const handleSaveItem = async (itemData) => {
    try {
      // Check if Supabase is configured
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        // Demo mode - just update state
        if (editingItem) {
          setMenuItems(prevItems =>
            prevItems.map(item => item.id === editingItem.id ? { ...item, ...itemData } : item)
          );
        } else {
          const newItem = {
            ...itemData,
            id: String(menuItems.length + 1),
            rating: 0,
            reviewsCount: 0
          };
          setMenuItems(prevItems => [...prevItems, newItem]);
        }
        setIsAddModalOpen(false);
        setEditingItem(null);
        return;
      }

      // Ensure images is an array
      const imagesArray = Array.isArray(itemData.images) 
        ? itemData.images.filter(img => img && typeof img === 'string' && img.trim() !== '')
        : [];

      console.log('Saving menu item:', {
        name: itemData.name,
        imagesCount: imagesArray.length,
        images: imagesArray
      });

      // Prepare data for Supabase
      const menuItemData = {
        name: itemData.name,
        category: itemData.category,
        description: itemData.description,
        price: itemData.price,
        cooking_time: itemData.cookingTime,
        images_urls: imagesArray, // Ensure it's an array
        tags: itemData.tags || [],
        customizations: itemData.customizations || {
          sizes: [],
          spiceLevels: [],
          addons: []
        },
        is_available: itemData.isAvailable,
        is_featured: itemData.isFeatured,
        is_popular: itemData.isPopular || false,
        is_new: itemData.isNew || false
      };

      if (editingItem) {
        // Update existing item
        const { data, error } = await supabase
          .from('menu_items')
          .update(menuItemData)
          .eq('id', editingItem.id)
          .select()
          .single();

        if (error) throw error;

        // Show success message
        setSuccessMessage(`"${itemData.name}" has been updated successfully!`);
        setShowSuccessToast(true);

        // Refresh the list
        await fetchMenuItems();
      } else {
        // This shouldn't happen since we navigate to /admin/menu/add for new items
        // But handle it just in case
        const { data, error } = await supabase
          .from('menu_items')
          .insert([menuItemData])
          .select()
          .single();

        if (error) throw error;

        // Show success message
        setSuccessMessage(`"${itemData.name}" has been added successfully!`);
        setShowSuccessToast(true);

        // Refresh the list
        await fetchMenuItems();
      }

      setIsAddModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving menu item:', error);
      setSuccessMessage('Failed to save menu item. Please try again.');
      setShowSuccessToast(true);
    }
  };

  const handleToggleAvailability = async (itemId) => {
    try {
      // Find the item to get current availability
      const item = menuItems.find(i => i.id === itemId);
      if (!item) return;

      const newAvailability = !item.isAvailable;

      // Check if Supabase is configured
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        // Demo mode - just update state
        setMenuItems(prevItems =>
          prevItems.map(item =>
            item.id === itemId ? { ...item, isAvailable: newAvailability } : item
          )
        );
        return;
      }

      // Update in Supabase
      const { error } = await supabase
        .from('menu_items')
        .update({ is_available: newAvailability })
        .eq('id', itemId);

      if (error) {
        throw error;
      }

      // Update state
      setMenuItems(prevItems =>
        prevItems.map(item =>
          item.id === itemId ? { ...item, isAvailable: newAvailability } : item
        )
      );
    } catch (error) {
      console.error('Error updating availability:', error);
      alert('Failed to update availability. Please try again.');
    }
  };

  if (!adminSession) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="menu-management">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="menu-main">
        <TopNavbar 
          onMenuClick={() => setSidebarOpen(true)}
          adminName={adminSession.username || adminSession.email}
        />
        
        <main className="menu-content">
          <div className="page-header">
            <div>
              <h1 className="page-title">Menu Management</h1>
              <nav className="breadcrumb">
                <span>Dashboard</span>
                <span className="breadcrumb-separator">/</span>
                <span>Menu</span>
              </nav>
            </div>
            <button className="add-item-btn" onClick={() => navigate('/admin/menu/add')}>
              ➕ Add New Item
            </button>
          </div>

          <CategoryTabs
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            onAddCategory={() => setIsCategoriesModalOpen(true)}
          />

          <SearchAndSortBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />

          {loading ? (
            <div className="loading-skeleton">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="skeleton-card" />
              ))}
            </div>
          ) : menuItems.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">🍽️</span>
              <p className="empty-text">No menu items yet</p>
              <p className="empty-subtext">Start by adding your first item</p>
              <button className="empty-add-btn" onClick={handleAddItem}>
                Add Item
              </button>
            </div>
          ) : (
            <div className={`menu-items-grid ${viewMode}`}>
              {menuItems.map(item => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  onEdit={() => handleEditItem(item)}
                  onDelete={() => handleDeleteClick(item)}
                  onToggleAvailability={() => handleToggleAvailability(item.id)}
                />
              ))}
            </div>
          )}

          {isAddModalOpen && (
            <AddEditItemModal
              item={editingItem}
              categories={categories.filter(c => c !== 'All')}
              onClose={() => {
                setIsAddModalOpen(false);
                setEditingItem(null);
              }}
              onSave={handleSaveItem}
            />
          )}

          {isCategoriesModalOpen && (
            <CategoriesModal
              categories={categories.filter(c => c !== 'All')}
              onClose={() => {
                setIsCategoriesModalOpen(false);
                fetchCategories(); // Refresh categories after modal closes
              }}
              onCategoryChange={handleCategoryChange}
            />
          )}

          <ConfirmModal
            isOpen={deleteConfirm.isOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete Menu Item"
            message={`Are you sure you want to delete "${deleteConfirm.item?.name}"? This action cannot be undone.`}
            confirmText="Delete"
            cancelText="Cancel"
            type="danger"
          />

          {showSuccessToast && (
            <SuccessToast
              message={successMessage}
              onClose={() => setShowSuccessToast(false)}
              duration={3000}
              type={successMessage.includes('Failed') ? 'error' : 'success'}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default MenuManagement;

